import React, { useState } from 'react';
import { makeStyles, Fab, Icon, Menu, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useDropdown } from '@moderepo/mode-app-components';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            position  : 'fixed',
            bottom    : theme.spacing(1),
            right     : theme.spacing(1),
            zIndex    : 1100,
            background: '#373838',
            color     : '#ffffff',

            '&:hover': {
                background: '#676767',
            },
            '&:active': {
                background: '#9e9e9e',
            },

            [theme.breakpoints.up('md')]: {
                bottom: theme.spacing(2),
                right : theme.spacing(2),
            },

            /*
            [theme.breakpoints.up('md')]: {
                position: 'relative',
                bottom  : 'unset',
                right   : 'unset',
            },
            */
        },

        menuOverlay: {
            background: 'rgba(0, 0, 0, 0)',
        },

        menu: {
            padding   : theme.spacing(0, 2),
            background: 'transparent',
            boxShadow : 'none',
            overflow  : 'hidden auto',
        },

        menuList: {
            display       : 'flex',
            padding       : 0,
            overflow      : 'visible',
            flexDirection : 'column',
            alignItems    : 'flex-end',
            justifyContent: 'flex-end',
            textAlign     : 'right',
        },

        menuItem: {
            margin       : theme.spacing(0.7, 0),
            padding      : theme.spacing(0.5, 2),
            height       : 'unset',
            textTransform: 'none',
            background   : '#373838',
            color        : '#ffffff',
            width        : 'fit-content',

            '& .icon': {
                color     : '#ffff00',
                display   : 'flex',
                alignItems: 'center',
                '&.start' : {
                    marginRight: theme.spacing(1),
                },
                '&.end': {
                    marginLeft: theme.spacing(1),
                },
            },

            '& span': {
                width: 'auto',
            },
            '&:hover': {
                background: '#676767',
            },
            '&:active': {
                background: '#9e9e9e',
            },
            '&:disabled': {
                background: '#e4e4e4',
            },
        },
    };
}, { name: 'FabMenu' });


export interface FabSubMenuItem<T> {
    readonly disabled?: boolean;
    readonly label: string;
    readonly value: T;
    readonly startIcon?: JSX.Element | undefined;
    readonly endIcon?: JSX.Element | undefined;
}


export interface FabMenuItem<T1, T2 = {}> extends FabSubMenuItem<T1> {
    readonly subMenuItems?: FabSubMenuItem<T2>[];
}



interface FabMenuProps<T1, T2 = {}> {
    readonly menuItems: FabMenuItem<T1, T2>[];
    readonly optimizeMenu?: boolean;                    // Default is true
    readonly onMenuItemClicked: (mainValue: T1, subMenuValue?: T2)=> void;
}


export const FabMenu = <T1, T2 = {}>(props: FabMenuProps<T1, T2>) => {
    const [menuAnchorEl, isMenuOpened, onMenuAnchorClicked, onMenuClosed] = useDropdown();
    const [selectedSubMenuIndex, setSelectedSubMenuIndex] = useState<number | undefined>(undefined);
    const subMenuItems = selectedSubMenuIndex !== undefined ? props.menuItems[selectedSubMenuIndex].subMenuItems : undefined;

    const classes = useStyle();

    return (
        <>
            <Fab
                className={classes.root}
                variant="round"
                color="primary"
                size="medium"
                onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                    // If there is only 1 menu item and optimizeMenu option is NOT false, we will optimize the menu and
                    // trigger the menuItemClicked on the first item as soon as the user click on the Fab button
                    if (props.menuItems.length === 1 && props.optimizeMenu !== false) {
                        props.onMenuItemClicked(props.menuItems[0].value);
                    } else {
                        onMenuAnchorClicked(event);
                    }
                }}
            >
                <Icon>
                    {/* If there is only 1 item and optimizeMenu is NOT false, we will show the item's icon instead */}
                    {props.menuItems.length === 1 && props.optimizeMenu !== false ? props.menuItems[0].startIcon : 'menu'}
                </Icon>
            </Fab>

            {/* If sub menu is not selected, show the main menu */}
            {selectedSubMenuIndex === undefined && (
                <Menu
                    className={clsx(classes.menuOverlay, 'main-menu')}
                    PaperProps={{
                        className: classes.menu,
                    }}
                    MenuListProps={{
                        className: classes.menuList,
                    }}

                    getContentAnchorEl={null}
                    anchorEl={menuAnchorEl}
                    keepMounted
                    anchorOrigin={{
                        vertical  : 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical  : 'top',
                        horizontal: 'right',
                    }}
                    open={isMenuOpened}
                    onClose={onMenuClosed}
                >
                    {props.menuItems.map((item: FabMenuItem<any, any>, index: number) => {
                        return (
                            <Fab
                                variant="extended"
                                key={`${item.value}-${item.label}`}
                                className={clsx(classes.menuItem)}
                                disabled={item.disabled}
                                color="inherit"
                                onClick={() => {
                                    if (item.subMenuItems) {
                                        // Close the main menu and open the sub menu
                                        const anchorEl = menuAnchorEl;
                                        onMenuClosed();
                                        setSelectedSubMenuIndex(index);

                                        // Manually trigger the anchor click event to have it show the sub menu
                                        (anchorEl as HTMLElement).click();
                                    } else {
                                        onMenuClosed();
                                        props.onMenuItemClicked(item.value);
                                    }
                                }}
                            >
                                {item.startIcon && (
                                    <span className="icon start">{item.startIcon}</span>
                                )}
                                {item.label}
                                {item.endIcon && (
                                    <span className="icon end">{item.endIcon}</span>
                                )}
                            </Fab>
                        );
                    })}
                </Menu>
            )}

            {/* If sub menu is selected, show the sub menu of the menu selected */}
            {selectedSubMenuIndex !== undefined && subMenuItems && (
                <Menu
                    className={clsx(classes.menuOverlay, `sub-menu-${selectedSubMenuIndex}`)}
                    PaperProps={{
                        className: classes.menu,
                    }}
                    MenuListProps={{
                        className: classes.menuList,
                    }}

                    getContentAnchorEl={null}
                    anchorEl={menuAnchorEl}
                    keepMounted
                    anchorOrigin={{
                        vertical  : 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical  : 'top',
                        horizontal: 'right',
                    }}
                    open={isMenuOpened}
                    onClose={() => {
                        setSelectedSubMenuIndex(undefined);
                        onMenuClosed();
                    }}
                >
                    {/* Add return to main menu button */}
                    <Fab
                        variant="round"
                        color="secondary"
                        size="small"
                        onClick={() => {
                            // On click, close the submenu and show the main menu
                            const anchorEl = menuAnchorEl;
                            onMenuClosed();
                            setSelectedSubMenuIndex(undefined);

                            // Manually trigger the anchor click event to have it show the main menu
                            (anchorEl as HTMLElement).click();
                        }}
                    >
                        <Icon>reply</Icon>
                    </Fab>
                    {subMenuItems.map((subItem: FabSubMenuItem<any>) => {
                        return (
                            <Fab
                                variant="extended"
                                key={`${subItem.value}-${subItem.label}`}
                                className={clsx(classes.menuItem)}
                                disabled={subItem.disabled}
                                color="inherit"
                                onClick={() => {
                                    setSelectedSubMenuIndex(undefined);
                                    onMenuClosed();
                                    props.onMenuItemClicked(props.menuItems[selectedSubMenuIndex].value, subItem.value);
                                }}
                            >
                                {subItem.startIcon && (
                                    <span className="icon start">{subItem.startIcon}</span>
                                )}
                                {subItem.label}
                                {subItem.endIcon && (
                                    <span className="icon end">{subItem.endIcon}</span>
                                )}
                            </Fab>
                        );
                    })}
                </Menu>
            )}
        </>
    );
};
